import { Logger } from '@egw/logger'
import { notifications, NOTIFICATIONS } from '@egw/notifications'
import { produce } from 'immer'
import isEqual from 'lodash.isequal'

const log = new Logger('Events Service')

/**
 * Type definitions for context
 */
interface DeviceContext {
  deviceData?: Record<string, unknown>
  platform?: Record<string, unknown>
  browserCompatibility?: Record<string, unknown>
  passkeyRecoveryAttemptUID?: string
  passkeySignInRequestUID?: string
  featureFlags?: Record<string, unknown>
  // Add other device-specific fields
}

interface IdentityContext {
  userId?: string
  email?: string
  firstName?: string
  lastName?: string
  phone?: string
  dateOfBirth?: string
  // Add other user-specific fields
}

/**
 * Events Service
 */
/**
 * Singleton class to handle events and context updates.
 * Maintains separate device and identity contexts.
 *
 * @example
 * const events = new EventsSingleton();
 * events.init();
 */
class EventsSingleton {
  private deviceContext: DeviceContext = {}
  private identityContext: IdentityContext = {}

  constructor() {
    if (!instance) {
      instance = this
    }

    // ===== DEVICE CONTEXT LISTENERS =====
    // These persist across sessions

    const deviceDataListener = (data: unknown): void => {
      const eventData = data as { detail: Record<string, unknown> }
      this.setDeviceContext({
        deviceData: {
          browserName: eventData.detail.browserName,
          browserVersion: eventData.detail.browserVersion,
          firstSeenAt: eventData.detail.firstSeenAt,
          visitorId: eventData.detail.visitorId,
          visitorFound: eventData.detail.visitorFound,
          lastSeenAt: eventData.detail.lastSeenAt,
          os: eventData.detail.os,
          osVersion: eventData.detail.osVersion,
          requestId: eventData.detail.requestId,
        },
      })
    }
    notifications.on(NOTIFICATIONS.DEVICE.DATA, deviceDataListener)

    const platformServiceListener = (data: unknown): void => {
      const eventData = data as { detail: Record<string, unknown> }
      this.setDeviceContext({ platform: eventData.detail })
    }
    notifications.on(NOTIFICATIONS.PLATFORM_SERVICE.INIT, platformServiceListener)

    const passkeyListener = (data: unknown): void => {
      const eventData = data as { detail: Record<string, unknown> }
      this.setDeviceContext({ browserCompatibility: eventData.detail })
    }
    notifications.on(NOTIFICATIONS.PASSKEY_SERVICE.INIT, passkeyListener)

    const featureFlagsListener = (data: unknown): void => {
      const eventData = data as { detail: { flags: Record<string, unknown> } }
      this.setDeviceContext({ featureFlags: eventData.detail.flags })
    }

    notifications.on(NOTIFICATIONS.LAUNCH_DARKLY.FLAGS_UPDATED, featureFlagsListener)

    // ===== IDENTITY CONTEXT LISTENERS =====
    // These are cleared on logout

    const userDataListener = (data: unknown): void => {
      const eventData = data as {
        detail: {
          customer: {
            email: string
            firstName?: string
            lastName?: string
            phone?: string
            dateOfBirth?: string
          }
        }
      }
      this.setIdentityContext({
        email: eventData.detail.customer.email,
        firstName: eventData.detail.customer.firstName,
        lastName: eventData.detail.customer.lastName,
        phone: eventData.detail.customer.phone,
        dateOfBirth: eventData.detail.customer.dateOfBirth,
      })
    }
    notifications.on(NOTIFICATIONS.API.CUSTOMER_DETAILS, userDataListener)

    const userIdListener = (data: unknown): void => {
      const eventData = data as { detail: { userId: string } }
      this.setIdentityContext({ userId: eventData.detail.userId })
    }
    notifications.on(NOTIFICATIONS.AUTH.AUTHENTICATED, userIdListener)

    // ===== SIGN OUT LISTENER =====
    // Clear identity context on logout
    const signOutListener = (): void => {
      log.debug('Sign out detected - clearing identity context')
      this.clearIdentityContext()
    }
    notifications.on(NOTIFICATIONS.AUTH.SIGN_OUT, signOutListener)

    // eslint-disable-next-line no-constructor-return
    return instance
  }

  init(): void {
    log.info('Events service is initialized')
  }

  /**
   * Set device context (persists across sessions)
   */
  setDeviceContext(context: Partial<DeviceContext>): void {
    const newContext = produce(this.deviceContext, (draft: DeviceContext) => {
      return {
        ...draft,
        ...context,
      }
    })

    // eslint-disable-next-line testing-library/no-debugging-utils
    log.debug(
      `SetDeviceContext Invoked: ${isEqual(this.deviceContext, newContext) ? 'Contexts equal, not updating' : 'Diff in context, updating'}`,
      {
        oldContext: this.deviceContext,
        newContext,
      },
    )

    if (!isEqual(this.deviceContext, newContext)) {
      this.deviceContext = newContext
      notifications.dispatch(NOTIFICATIONS.EVENTS.SET_DEVICE_CONTEXT, context)

      // Also dispatch legacy SET_CONTEXT for backward compatibility
      notifications.dispatch(NOTIFICATIONS.EVENTS.SET_CONTEXT, context)
    }
  }

  /**
   * Set identity context (cleared on logout)
   */
  setIdentityContext(context: Partial<IdentityContext>): void {
    const newContext = produce(this.identityContext, (draft: IdentityContext) => {
      return {
        ...draft,
        ...context,
      }
    })

    // eslint-disable-next-line testing-library/no-debugging-utils
    log.debug(
      `SetIdentityContext Invoked: ${isEqual(this.identityContext, newContext) ? 'Contexts equal, not updating' : 'Diff in context, updating'}`,
      {
        oldContext: this.identityContext,
        newContext,
      },
    )

    if (!isEqual(this.identityContext, newContext)) {
      this.identityContext = newContext
      notifications.dispatch(NOTIFICATIONS.EVENTS.SET_IDENTITY_CONTEXT, context)

      // Also dispatch legacy SET_CONTEXT for backward compatibility
      notifications.dispatch(NOTIFICATIONS.EVENTS.SET_CONTEXT, context)
    }
  }

  /**
   * Clear identity context (called on logout)
   */
  clearIdentityContext(): void {
    log.debug('Clearing identity context', { previousContext: this.identityContext })

    this.identityContext = {}
    notifications.dispatch(NOTIFICATIONS.EVENTS.CLEAR_IDENTITY_CONTEXT)
  }

  /**
   * Get combined context (for backward compatibility)
   */
  getContext(): Record<string, unknown> {
    return {
      ...this.deviceContext,
      ...this.identityContext,
    }
  }

  /**
   * Get device context only
   */
  getDeviceContext(): DeviceContext {
    return this.deviceContext
  }

  /**
   * Get identity context only
   */
  getIdentityContext(): IdentityContext {
    return this.identityContext
  }

  /**
   * Send event with full context
   */
  event(eventName: string, eventData?: Record<string, unknown>): void {
    // eslint-disable-next-line testing-library/no-debugging-utils
    log.debug('Event:', eventName, eventData)

    notifications.dispatch(NOTIFICATIONS.EVENTS.EVENT, {
      eventName,
      props: eventData,
      // Send both contexts separately
      deviceContext: this.deviceContext,
      identityContext: this.identityContext,
      // Maintain backward compatibility with merged context
      context: this.getContext(),
    })
  }
}

let instance: EventsSingleton | null = null

/**
 * Events singleton class
 */
export const eventsService = new EventsSingleton()

/**
 * Sends an event with the given name and optional data.
 *
 * @param eventName - The name of the event.
 * @param eventData - Optional data to be included with the event.
 */
export const sendEvent = (eventName: string, eventData?: Record<string, unknown>): void => {
  eventsService.event(eventName, eventData)
}

/**
 * @deprecated Use setDeviceContext or setIdentityContext instead
 * Updates the context with the provided key and value.
 * For backward compatibility, this intelligently routes to the correct context type.
 *
 * @param context - The context to be set.
 */
export const setContext = (context: Record<string, unknown>): void => {
  log.warn('setContext() is deprecated. Use setDeviceContext() or setIdentityContext() instead.')

  // For backward compatibility, try to intelligently route to correct context
  // Determine if this is device or identity context based on keys
  const deviceKeys = ['deviceData', 'platform', 'browserCompatibility']
  const identityKeys = ['userId', 'email', 'firstName', 'lastName', 'phone', 'dateOfBirth']

  const hasDeviceKeys = Object.keys(context).some((key) => deviceKeys.includes(key))
  const hasIdentityKeys = Object.keys(context).some((key) => identityKeys.includes(key))

  if (hasDeviceKeys) {
    eventsService.setDeviceContext(context)
  }
  if (hasIdentityKeys) {
    eventsService.setIdentityContext(context)
  }
}

/**
 * Sets device context (persists across sessions).
 *
 * @param context - The device context to be set.
 */
export const setDeviceContext = (context: Partial<DeviceContext>): void => {
  eventsService.setDeviceContext(context)
}

/**
 * Sets identity context (cleared on logout).
 *
 * @param context - The identity context to be set.
 */
export const setIdentityContext = (context: Partial<IdentityContext>): void => {
  eventsService.setIdentityContext(context)
}

/**
 * Clears identity context (called on logout).
 */
export const clearIdentityContext = (): void => {
  eventsService.clearIdentityContext()
}

/**
 * Gets device context.
 *
 * @returns The current device context.
 */
export const getDeviceContext = (): DeviceContext => {
  return eventsService.getDeviceContext()
}

/**
 * Gets identity context.
 *
 * @returns The current identity context.
 */
export const getIdentityContext = (): IdentityContext => {
  return eventsService.getIdentityContext()
}

/**
 * Type exports
 */
export type { DeviceContext, IdentityContext }
